import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Menu,
  Paper,
  Popover,
  Select,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import {
  IconAlertCircle,
  IconArchive,
  IconChevronDown,
  IconClockShare,
  IconCopy,
  IconHexagons,
  IconLock,
  IconSend2,
  IconSettingsCog,
  IconTrash,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { MRT_ColumnDef, MRT_Row } from 'mantine-react-table'
import { createMRTColumnHelper } from 'mantine-react-table'
import { observer } from 'mobx-react-lite'
import { usePostHog } from 'posthog-js/react'
import { parse } from 'qs'
import { createContext, Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSearch } from 'wouter'
import { navigate } from 'wouter/use-browser-location'

import { Screen } from '@/components/Screen'
import type { rowActionsIconRegistry, TableProps } from '@/components/Table'
import { HeaderCellWithTooltip, Table, TableRowActionButton } from '@/components/Table'
import { TruncatedText } from '@/components/TruncatedText'
import { UserDisplay } from '@/components/UserDisplay'
import { theme } from '@/configs/theme'
import { MSK_NAME } from '@/constants/contentModules'
import { useAllContentCreators } from '@/hooks/useAllContentCreators'
import type { ESKWithId } from '@/hooks/useAllEsks'
import { useAllEsks } from '@/hooks/useAllEsks'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useGuestUserAccess } from '@/hooks/useGuestUserAccess'
import { useSetupProgressTracker } from '@/hooks/useSetupProgressTracker'
import { useTopicsEnabled } from '@/hooks/useTopicsEnabled'
import type {
  GetContentModuleResponse,
  GetContentModulesResponse,
  GetUsersResponseEntry,
} from '@/services/api/api.types'
import type { ContentModuleStatus } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import {
  canSponsorAdminDelete,
  canSponsorAdminEdit,
  canSponsorAdminMoveToDraft,
  canSponsorAdminMoveToPendingApproval,
  canSponsorAdminMoveToTesting,
  canSponsorAdminPublish,
  type CMTableEntry,
  smeTables,
  sponsorAdminTables,
} from '@/utils/contentModuleTables'
import { isOwnerOrAdditionalOwner, toUpdateOwnersPayload } from '@/utils/contentModuleUtils'
import { useNavigateTo } from '@/utils/navigation'
import { getRoutePath } from '@/utils/navigation'

import { AdditionalOwnersModal } from './AdditionalOwnersModal'
import { markLockJustAcquired } from './ContentModuleFormScreen/lockHandshake'
import { PriorityScreen } from './PriorityScreen'

type RowActionsAndLabels = {
  actions: Array<keyof typeof rowActionsIconRegistry>
  labels: Partial<Record<keyof typeof rowActionsIconRegistry, string>>
  lockedByOther?: { userId: string; name: string; email: string } | null
}

const getUserOwnerEntry = (cm: GetContentModuleResponse, userId: string) => {
  return cm.additionalOwners?.find(o => o.userId === userId)
}

// PMs get hot-edit by role regardless of the stored flag. ActionsSection
// self-heals the stored flag for PM owners on save so the owners modal stays
// in sync with what the gate here already allows.
const userCanHotEdit = (cm: GetContentModuleResponse, userId: string, isSponsorAdmin: boolean): boolean => {
  if (isSponsorAdmin) return true
  return getUserOwnerEntry(cm, userId)?.hotEdit ?? false
}

const rowActionsAndLabels = (
  row: MRT_Row<GetContentModuleResponse>,
  userId: string,
  isSponsorAdmin: boolean,
): RowActionsAndLabels => {
  const cm = row.original
  const isOwner = isOwnerOrAdditionalOwner(cm, userId)

  const editable = isSponsorAdmin
    ? canSponsorAdminEdit(cm.status as ContentModuleStatus, isOwner, cm.allowedForAllSponsors ?? false, cm.editable)
    : isOwner

  const isLive = cm.status === 'published'
  const canEdit = editable && !(isLive && !userCanHotEdit(cm, userId, isSponsorAdmin))

  const lockedByOther = cm.lockedBy?.userId && cm.lockedBy.userId !== userId ? cm.lockedBy : null

  const result: RowActionsAndLabels = canEdit
    ? lockedByOther
      ? {
          actions: ['lock', 'view'],
          labels: { lock: `Locked by ${lockedByOther.name || lockedByOther.email}`, view: 'View module' },
          lockedByOther,
        }
      : { actions: ['edit', 'view'], labels: { edit: 'Edit Module', view: 'View module' } }
    : { actions: ['view'], labels: { view: 'View module' } }

  if (isSponsorAdmin) {
    result.actions.unshift('owners')
    result.labels.owners = 'Manage Owners'
  }

  return result
}

function LockPopover({
  lockedBy,
  isSponsorAdmin,
  onForceUnlock,
}: {
  lockedBy: { userId: string; name: string; email: string }
  isSponsorAdmin: boolean
  onForceUnlock: () => void
}) {
  return (
    <Popover position="top" withArrow shadow="md">
      <Popover.Target>
        <ActionIcon variant="subtle" color="gray" aria-label={`Module locked by ${lockedBy.email}`}>
          <IconLock size={18} stroke={1.5} aria-hidden="true" />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="sm">
            Being edited by{' '}
            <Text span fw={600}>
              {lockedBy.email}
            </Text>
          </Text>
          {isSponsorAdmin && (
            <Button size="xs" color="red" variant="light" onClick={onForceUnlock}>
              Force Unlock
            </Button>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

type TopicOption = { value: string; label: string }

type MediaAccessValue = 'subscriber' | 'free'

type TableContext = {
  esks: ESKWithId[]
  isEskDataReady: boolean
  changeModuleStatus: (data: GetContentModulesResponse[0], status: `${ContentModuleStatus}`) => void
  copyContentModule: (taskId: string) => void
  users: GetUsersResponseEntry[]
  topicsData: TopicOption[]
  updateTopic: (taskId: string, topic: string | null) => void
  updatingTopicIds: Set<string>
  deletingModules: Set<string>
  updateMediaAccess: (taskId: string, value: MediaAccessValue) => void
  isUpdatingMediaAccess: boolean
}
const TableContext = createContext<TableContext>({} as TableContext)
const useTableContext = () => useContext(TableContext)

// ContentModulesScreen Component
// --------------------------------------------------------------------------------
export const ContentModulesScreen = observer(function ContentModulesScreen() {
  const { api, authenticationStore, uiStore, devStore } = useGlobalState()
  const { user, isSponsorAdmin } = authenticationStore
  const posthog = usePostHog()
  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useCheckMobileScreen()

  // Get setup progress tracker for marking completion
  const { markStepComplete } = useSetupProgressTracker()
  const [activeTab, setActiveTab] = useState<string | null>(isSponsorAdmin ? 'all-modules' : 'my-modules')
  const searchParamsString = useSearch()
  const searchParams: { tab?: string } = parse(searchParamsString)

  // Set active tab from URL parameter if provided
  useEffect(() => {
    if (searchParams.tab) {
      setActiveTab(searchParams.tab)
    }
  }, [searchParams.tab])

  useEffect(() => {
    posthog?.capture('$pageview', { skPlatform: 'portal' })
  }, [posthog])
  const navigateTo = useNavigateTo()

  const { allEsks, isReady: isEskDataReady, sidekicksQuery } = useAllEsks()

  const { allContentCreators, isLoaded: usersLoaded } = useAllContentCreators()

  // Track modules that we've requested deletion for and are waiting for the backend to confirm
  const [deletingModules, setDeletingModules] = useState<Set<string>>(new Set())
  const deletingTimestamps = useRef<Map<string, number>>(new Map())

  const cmQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    enabled: sidekicksQuery.isSuccess && usersLoaded,
    refetchInterval: deletingModules.size > 0 ? 2 * 1000 : 7 * 1000,
  })

  const isDataLoading = !cmQuery.data

  const { topicsEnabled, hasDefinedTopics, topics } = useTopicsEnabled()
  const { guestUserAccess } = useGuestUserAccess()

  const topicsData = useMemo((): TopicOption[] => {
    return topics.map(t => ({ value: t.displayName, label: t.displayName }))
  }, [topics])

  // Per-row tracking so a single topic edit doesn't freeze every other
  // Topic dropdown in the table.
  const [updatingTopicIds, setUpdatingTopicIds] = useState<Set<string>>(new Set())

  const updateTopicMutation = useMutation({
    mutationFn: async ({ taskId, topic }: { taskId: string; topic: string | null }) => {
      return api.updateContentModule({
        task_id: taskId,
        categories: topic ? [topic] : [],
        // Inline edit from the CM table is outside an edit-lock session, so
        // bypass the backend lock check. Otherwise this fails whenever someone
        // else has the module open in the editor.
        lock: false,
      })
    },
    onMutate: ({ taskId, topic }) => {
      setUpdatingTopicIds(prev => {
        const next = new Set(prev)
        next.add(taskId)
        return next
      })

      const previous = api.queryClient.getQueryData<GetContentModulesResponse>(['cm', 'list'])

      api.queryClient.setQueryData<GetContentModulesResponse>(['cm', 'list'], old =>
        old?.map(cm => (cm.taskId === taskId ? { ...cm, categories: topic ? [topic] : [] } : cm)),
      )

      // Fire-and-forget. TanStack marks the query as cancelled synchronously,
      // so an in-flight ['cm','list'] poll resolving after this line won't
      // commit its (stale) result over our patch. We don't await because
      // api.request doesn't plumb AbortSignal to axios, so the returned
      // promise would block on the in-flight fetch -- the bug we're fixing.
      api.queryClient.cancelQueries({ queryKey: ['cm', 'list'] })

      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        api.queryClient.setQueryData(['cm', 'list'], ctx.previous)
      }
      notifications.show({
        title: 'Error',
        message: 'Failed to update topic. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
    onSettled: (_data, _err, vars) => {
      setUpdatingTopicIds(prev => {
        if (!prev.has(vars.taskId)) return prev
        const next = new Set(prev)
        next.delete(vars.taskId)
        return next
      })
      // Mark stale only; the existing 7s poll reconciles. Refetching here
      // would burn a full list payload and could clobber other in-flight
      // optimistic edits.
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'], refetchType: 'none' })
    },
  })

  const updateTopic = (taskId: string, topic: string | null) => {
    updateTopicMutation.mutate({ taskId, topic })
  }

  // Media Access: PMs only, on media-mode channels only. Edited inline from
  // the table outside an edit-lock session, so we send lock=false (matches the
  // Topic dropdown above).
  const updateMediaAccessMutation = useMutation({
    mutationFn: async ({ taskId, value }: { taskId: string; value: MediaAccessValue }) => {
      return api.updateContentModule({
        task_id: taskId,
        subscription_only: value === 'subscriber',
        lock: false,
      })
    },
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update media access. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const updateMediaAccess = (taskId: string, value: MediaAccessValue) => {
    updateMediaAccessMutation.mutate({ taskId, value })
  }

  const updateOwnersMutation = useMutation({
    mutationFn: api.updateAdditionalOwners,
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
      modals.closeAll()
      notifications.show({
        title: 'Success',
        message: 'Additional owners updated.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update owners. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  // Media Access column: PM-only, and only when Guest User Access is on. Hidden
  // on the Help Modules tab (help content isn't subscription-gated).
  const showMediaAccessColumn = guestUserAccess && isSponsorAdmin

  // Hide Topic column on Help Modules tab (help modules don't get topics); show it on other tabs when topics are enabled
  const columnVisibilityDefault = useMemo(
    () => ({
      [ColId.Topic]: topicsEnabled && hasDefinedTopics,
      [ColId.MediaAccess]: showMediaAccessColumn,
    }),
    [topicsEnabled, hasDefinedTopics, showMediaAccessColumn],
  )
  const columnVisibilityHelpModules = useMemo(() => ({ [ColId.Topic]: false, [ColId.MediaAccess]: false }), [])

  // Track modules that we've published and are waiting for processing to complete
  const [publishingModules, setPublishingModules] = useState<Set<string>>(new Set())

  // Monitor modules we're tracking for when they finish processing
  useEffect(() => {
    if (!cmQuery.data || publishingModules.size === 0) return

    cmQuery.data.forEach(cm => {
      if (!cm.taskId || !publishingModules.has(cm.taskId)) return

      // Module finished processing (was processing, now it's not)
      if (!cm.inProcess) {
        // Check if there was an error
        if (cm.error?.hasError) {
          notifications.show({
            title: 'Content Module Publishing Failed',
            message: (
              <Text size="sm">
                There was an error publishing your content module. Please try again or contact support at{' '}
                <a href="mailto:support@sidekickplatform.com" style={{ color: 'inherit', textDecoration: 'underline' }}>
                  support@sidekickplatform.com
                </a>
              </Text>
            ),
            color: 'red',
            classNames: commonClasses.notificationFilled,
            position: 'bottom-left',
            autoClose: 10000,
          })
        }
        // Remove from tracking
        setPublishingModules(prev => {
          const next = new Set(prev)
          next.delete(cm.taskId!)
          return next
        })
      }
    })
  }, [cmQuery.data, publishingModules])

  // Monitor modules we've requested deletion for until they disappear from the list
  useEffect(() => {
    if (deletingModules.size === 0) return

    const currentTaskIds = new Set(cmQuery.data?.map(cm => cm.taskId).filter(Boolean) ?? [])
    const toRemove: string[] = []

    deletingModules.forEach(taskId => {
      const gone = !currentTaskIds.has(taskId)
      const timedOut = Date.now() - (deletingTimestamps.current.get(taskId) ?? 0) > 30_000

      if (gone) {
        toRemove.push(taskId)
      } else if (timedOut) {
        toRemove.push(taskId)
        notifications.show({
          title: 'Module Deletion May Not Have Completed',
          message: 'The module may not have been deleted. Please refresh the page and try again, or contact support.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
          autoClose: 10000,
        })
      }
    })

    if (toRemove.length > 0) {
      setDeletingModules(prev => {
        const next = new Set(prev)
        toRemove.forEach(id => {
          next.delete(id)
          deletingTimestamps.current.delete(id)
        })
        return next
      })
    }
  }, [cmQuery.data, deletingModules])

  const cmChangeStatusMutation = useMutation({
    mutationFn: api.changeContentModuleStatus,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: (d, _vars) => {
      const success = 'status' in d && d.status
      const error = 'errors' in d

      if (success) {
        api.queryClient.invalidateQueries({
          queryKey: ['cm', 'list'],
        })

        setTimeout(() => {
          cmQuery.refetch()
        }, 1000)

        // Mark createContent step complete if this was a publish action
        if (_vars.status === 'published') {
          markStepComplete('createContent')
        }

        if (_vars.status === 'removed' && _vars.task_id) {
          setDeletingModules(prev => new Set(prev).add(_vars.task_id))
          deletingTimestamps.current.set(_vars.task_id, Date.now())
        }

        // DISABLED: As per request from Jeff: https://infinitered.slack.com/archives/C07MY1RQ63S/p1734730558931089?thread_ts=1734729429.572509&cid=C07MY1RQ63S
        // notifications.show({
        //   title: 'Module Status Updated',
        //   message: d.message ?? `The module's status is now "${vars.status}".`,
        //   color: 'green',
        //   classNames: commonClasses.notificationFilled,
        //   position: 'bottom-left',
        // })
      } else if (error) {
        const errorMessage =
          d.errors?.map((e, i) => (
            <Fragment key={i}>
              <br />
              {e.error}
            </Fragment>
          )) ?? 'There was an issue attempting to update the module status. Please try again or contact support.'

        notifications.show({
          title: 'Could Not Update Module Status',
          message: errorMessage,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    },
    onError: () => {
      notifications.show({
        title: 'Could Not Update Module Status',
        message: 'There was an issue attempting to update the module status. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const copyContentModuleMutation = useMutation({
    mutationFn: api.copyContentModule,
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
      notifications.show({
        title: 'Module Copied',
        message: 'The module was successfully copied.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
    onError: () => {
      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue copying the module. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  function copyContentModule(taskId: string) {
    copyContentModuleMutation.mutate({ task_id: taskId })
  }

  function changeModuleStatus(data: GetContentModulesResponse[0], status: `${ContentModuleStatus}`) {
    const mutate = () => {
      // Track this module if we're publishing it
      if (status === 'published' && data.taskId) {
        setPublishingModules(prev => new Set(prev).add(data.taskId!))
      }
      cmChangeStatusMutation.mutate({ task_id: data.taskId!, status })
    }

    if (status === 'published') {
      mutate()
      return
    }

    if (status === 'draft' || status === 'removed' || status === 'testing') {
      const title = status === 'draft' ? 'Unpublish Module?' : status === 'testing' ? 'Test Module?' : 'Delete Module?'
      const message =
        status === 'draft'
          ? 'Are you sure you want to change this module\'s status to "Draft"?'
          : status === 'testing'
            ? 'You—and only you—can now interact with your content in the Sidekick app. No one else will be able to interact with this content until you publish it.'
            : 'Are you sure you want to delete this module? This action cannot be undone.'
      const color = status === 'draft' ? 'orange' : status === 'testing' ? undefined : 'red'
      const label = status === 'draft' ? 'Change to Draft' : status === 'testing' ? 'Test in App' : 'Delete Module'

      modals.openConfirmModal({
        title,
        centered: true,
        children: <Text size="sm">{message}</Text>,
        labels: { confirm: label, cancel: 'Cancel' },
        confirmProps: { color },
        groupProps: { mt: 'xl' },
        onConfirm: mutate,
      })

      return
    }

    if (status === 'pending_approval') {
      if (data.status === 'published') {
        const hasDirectPublish = getUserOwnerEntry(data, user?.userId ?? '')?.directPublish ?? false
        if (isSponsorAdmin || hasDirectPublish) {
          modals.openConfirmModal({
            title: 'Change to Pending?',
            centered: true,
            children: <Text size="sm">{`Are you sure you want to change this module's status to "Pending"?`}</Text>,
            labels: { confirm: 'Change to Pending', cancel: 'Cancel' },
            confirmProps: { color: 'orange' },
            groupProps: { mt: 'xl' },
            onConfirm: mutate,
          })
          return
        }
      }

      modals.openConfirmModal({
        title: 'Ready to Submit?',
        centered: true,
        children: <Text size="sm">{`Are you ready to submit for approval?`}</Text>,
        labels: { confirm: 'Yes', cancel: 'Cancel' },
        groupProps: { mt: 'xl' },
        onCancel: modals.closeAll,
        onConfirm: () => {
          modals.openConfirmModal({
            title: 'Copyright Confirmation',
            centered: true,
            children: (
              <Text size="sm">{`I verify that all the information provided for this module belongs to me and my organization or is used with permission from the owner.`}</Text>
            ),
            labels: { confirm: 'Confirm and Submit', cancel: 'Cancel' },
            groupProps: { mt: 'xl' },
            onCancel: modals.closeAll,
            onConfirm: mutate,
          })
        },
      })
    }
  }

  const data = useMemo(() => {
    if (!cmQuery.data) return []
    return devStore.showRemovedModules ? cmQuery.data : cmQuery.data.filter(d => d.status !== 'removed')
  }, [cmQuery.data, devStore.showRemovedModules])

  const cmCreateMutation = useMutation({
    mutationFn: api.createContentModule,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => {
      uiStore.hideLoadingOverlay()
      setIsLoading(false)
    },
    onSuccess: data => {
      navigate(getRoutePath('contentModuleFormEdit', { id: data.taskId }), { replace: true })
      /*
      notifications.show({
        title: 'Module Created',
        message: 'The module was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      */
    },
    onError: () => {
      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the module. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const handleRowActionClick = (
    action: keyof typeof rowActionsIconRegistry,
    row: MRT_Row<GetContentModuleResponse>,
  ) => {
    if (deletingModules.has(row.original.taskId ?? '')) return
    if (action === 'edit') {
      const taskId = row.original.taskId!
      uiStore.showLoadingOverlay()
      api
        .lockContentModule({ task_id: taskId })
        .then(() => {
          markLockJustAcquired(taskId)
          posthog?.capture('content_module_edit', { taskId, skPlatform: 'portal' })
          navigateTo('contentModuleFormEdit', { id: taskId })
          uiStore.setIsNewContentModule(false)
        })
        .catch(() => {
          notifications.show({
            title: 'Module Locked',
            message: 'This module is currently being edited by someone else. Please try again later.',
            color: 'red',
            classNames: commonClasses.notificationFilled,
            position: 'bottom-left',
          })
          api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
        })
        .finally(() => uiStore.hideLoadingOverlay())
    } else if (action === 'view') {
      posthog?.capture('content_module_view', { taskId: row.original.taskId!, skPlatform: 'portal' })
      navigateTo('contentModule', { id: row.original.taskId! })
    } else if (action === 'owners') {
      const cm = row.original
      const primaryOwner = allContentCreators.find(u => u.userId === cm.createdBy)
      modals.open({
        withCloseButton: false,
        centered: true,
        size: theme.rem(1200),
        styles: {
          body: { display: 'flex', flexDirection: 'column' as const, maxHeight: '80vh', overflow: 'hidden' },
        },
        children: (
          <AdditionalOwnersModal
            users={allContentCreators}
            currentOwners={cm.additionalOwners ?? []}
            primaryOwnerId={cm.createdBy ?? ''}
            primaryOwnerName={primaryOwner?.name || ''}
            primaryOwnerEmail={primaryOwner?.email ?? cm.owner ?? ''}
            primaryOwnerIsPm={primaryOwner?.isSponsorAdmin ?? false}
            onSave={owners => {
              updateOwnersMutation.mutate({
                task_id: cm.taskId!,
                additional_owners: toUpdateOwnersPayload(owners),
              })
            }}
            onClose={() => modals.closeAll()}
          />
        ),
      })
    }
  }

  const CommonTableProps = (
    columnVisibility: Record<string, boolean>,
  ): Omit<TableProps<GetContentModulesResponse[0]>, 'data' | 'columns'> => ({
    entity: { singular: 'Module', plural: 'Modules', Icon: IconHexagons },
    enableSorting: true,
    initialState: { sorting: [{ id: ColId.Status, desc: true }] },
    state: { columnVisibility, isLoading: isDataLoading },
    positionActionsColumn: 'first',
    EmptyStateProps: { py: 'xl' },
    renderRowActions: ({ row }) => {
      if (deletingModules.has(row.original.taskId ?? '')) return null
      const result = rowActionsAndLabels(row, user?.userId ?? '', isSponsorAdmin)
      const { actions, labels, lockedByOther } = result

      return (
        <Group gap="xs" justify="flex-end" flex="1 1 100%" wrap="nowrap">
          {actions.map(action => {
            if (action === 'lock' && lockedByOther) {
              return (
                <LockPopover
                  key="lock"
                  lockedBy={lockedByOther}
                  isSponsorAdmin={isSponsorAdmin}
                  onForceUnlock={() => {
                    modals.openConfirmModal({
                      title: 'Force Unlock Module',
                      centered: true,
                      children: (
                        <Text size="sm">
                          This module is currently being edited by{' '}
                          <Text span fw={600}>
                            {lockedByOther.name || lockedByOther.email}
                          </Text>
                          . Force unlocking may cause them to lose unsaved changes. Are you sure?
                        </Text>
                      ),
                      labels: { confirm: 'Force Unlock', cancel: 'Cancel' },
                      confirmProps: { color: 'red' },
                      onConfirm: () => {
                        api
                          .unlockContentModule({ task_id: row.original.taskId!, force_unlock: true })
                          .then(() => {
                            api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
                            notifications.show({
                              title: 'Module Unlocked',
                              message: 'The module has been force unlocked.',
                              color: 'green',
                              classNames: commonClasses.notificationFilled,
                              position: 'bottom-left',
                            })
                          })
                          .catch(() => {
                            notifications.show({
                              title: 'Error',
                              message: 'Failed to unlock the module. Please try again.',
                              color: 'red',
                              classNames: commonClasses.notificationFilled,
                              position: 'bottom-left',
                            })
                          })
                      },
                    })
                  }}
                />
              )
            }
            return (
              <TableRowActionButton
                key={action}
                action={action}
                tooltipLabel={labels[action]}
                onClick={() => handleRowActionClick(action, row)}
              />
            )
          })}
        </Group>
      )
    },
    onToolbarActionClick: action => {
      if (action === 'new' && !isLoading) {
        setIsLoading(true)
        cmCreateMutation?.mutateAsync({ content: ' ' })
        uiStore.setIsNewContentModule(true)
      }
    },
  })

  const getTables = (isSponsorAdminValue: boolean): CMTableEntry[] => {
    return isSponsorAdminValue ? sponsorAdminTables : smeTables
  }

  // Dropdown options for mobile view
  const tabOptions = useMemo(() => {
    const tables = getTables(isSponsorAdmin)
    const options = tables.map(entry => ({
      value: entry.title.toLowerCase().replace(' ', '-'),
      label: isDataLoading
        ? entry.title
        : `${entry.title} (${data.filter(item => entry.filterFn(item, isOwnerOrAdditionalOwner(item, user?.userId ?? ''))).length})`,
    }))

    if (isSponsorAdmin) {
      options.push({
        value: 'cm-prioritization',
        label: 'Module Prioritization',
      })
    }

    return options
  }, [isSponsorAdmin, data, user?.userId, isDataLoading])

  const sponsorAdminText = `The content library contains all the content modules in your Sidekick channel.`

  const smeText = `The content library contains all the content modules in your organization's Sidekick account.`

  return (
    <Screen title="Content Library" icon={IconHexagons}>
      <Stack gap="md">
        <Text size="md" c="black" mb="md" style={{ whiteSpace: 'pre-line' }}>
          {isSponsorAdmin ? sponsorAdminText : smeText}
        </Text>
        <TableContext.Provider
          value={{
            changeModuleStatus,
            copyContentModule,
            esks: allEsks,
            isEskDataReady,
            users: allContentCreators,
            topicsData,
            updateTopic,
            updatingTopicIds,
            deletingModules,
            updateMediaAccess,
            isUpdatingMediaAccess: updateMediaAccessMutation.isPending,
          }}>
          {isMobile ? (
            <>
              <Select
                value={activeTab}
                onChange={setActiveTab}
                data={tabOptions}
                size="lg"
                fw={500}
                placeholder="Select a section"
              />

              {/* Tab panels for mobile */}
              <div style={{ paddingTop: '1rem' }}>
                {getTables(isSponsorAdmin).map(entry => {
                  const tabValue = entry.title.toLowerCase().replace(' ', '-')
                  const isActive = activeTab === tabValue

                  return isActive ? (
                    <Paper key={entry.title} radius="md" p="lg" shadow="sm">
                      <Table
                        data={data.filter(item =>
                          entry.filterFn(item, isOwnerOrAdditionalOwner(item, user?.userId ?? '')),
                        )}
                        columns={cols}
                        searchLabel={`Search ${entry.title}`}
                        toolbarActionsInclude={entry.toolbarActionsInclude}
                        displayColumnDefOptions={entry.displayColumnDefOptions}
                        {...CommonTableProps(
                          entry.hideTopicColumn ? columnVisibilityHelpModules : columnVisibilityDefault,
                        )}
                      />
                    </Paper>
                  ) : null
                })}

                {/* Module Prioritization panel for mobile */}
                {activeTab === 'cm-prioritization' && <PriorityScreen />}
              </div>
            </>
          ) : (
            <Tabs value={activeTab} onChange={setActiveTab}>
              <Tabs.List>
                {getTables(isSponsorAdmin).map(entry => (
                  <Tabs.Tab
                    key={entry.title}
                    value={entry.title.toLowerCase().replace(' ', '-')}
                    styles={{
                      tab: {
                        fontSize: '1.1rem',
                        fontWeight: 500,
                      },
                    }}>
                    {isDataLoading
                      ? entry.title
                      : `${entry.title} (${data.filter(item => entry.filterFn(item, isOwnerOrAdditionalOwner(item, user?.userId ?? ''))).length})`}
                  </Tabs.Tab>
                ))}
                {isSponsorAdmin && (
                  <Tabs.Tab
                    key="cm-prioritization"
                    value="cm-prioritization"
                    styles={{ tab: { fontSize: '1.1rem', fontWeight: 500 } }}>
                    Module Prioritization
                  </Tabs.Tab>
                )}
              </Tabs.List>

              {/* Tab panels for desktop */}
              {getTables(isSponsorAdmin).map(entry => (
                <Tabs.Panel key={entry.title} value={entry.title.toLowerCase().replace(' ', '-')} pt="md">
                  <Paper radius="md" p="lg" shadow="sm">
                    <Table
                      data={data.filter(item =>
                        entry.filterFn(item, isOwnerOrAdditionalOwner(item, user?.userId ?? '')),
                      )}
                      columns={cols}
                      searchLabel={`Search ${entry.title}`}
                      toolbarActionsInclude={entry.toolbarActionsInclude}
                      displayColumnDefOptions={entry.displayColumnDefOptions}
                      {...CommonTableProps(
                        entry.hideTopicColumn ? columnVisibilityHelpModules : columnVisibilityDefault,
                      )}
                    />
                  </Paper>
                </Tabs.Panel>
              ))}
              <Tabs.Panel key="cm-prioritization" value="cm-prioritization" pt="md">
                <PriorityScreen />
              </Tabs.Panel>
            </Tabs>
          )}
        </TableContext.Provider>
      </Stack>
    </Screen>
  )
})

// Table Columns and Menu Actions
// --------------------------------------------------------------------------------
enum ColId {
  Title = 'title',
  Topic = 'topic',
  SubTopic = 'sub-topic',
  EskName = 'esk-name',
  Status = 'status',
  HumanExpert = 'human-expert',
  MediaAccess = 'media-access',
}

type OwnerCellValue = {
  display: string
  name: string
  email: string
  additionalOwners: NonNullable<GetContentModuleResponse['additionalOwners']>
}

const MAX_VISIBLE_ADDITIONAL_OWNERS = 2

function OwnerCell({ value, showDetailedView }: { value: OwnerCellValue; showDetailedView: boolean }) {
  const visible = value.additionalOwners.slice(0, MAX_VISIBLE_ADDITIONAL_OWNERS)
  const overflowCount = value.additionalOwners.length - visible.length

  return (
    <Box w="100%" p={0}>
      <UserDisplay user={value} showDetailedView={showDetailedView} />
      {visible.map(ao => {
        const displayName = ao.name || ao.email || ao.userId
        return (
          <Box key={ao.userId} style={{ lineHeight: 1.3, overflow: 'hidden' }} mt={4}>
            <TruncatedText size="sm" c="dimmed" fw={600}>
              {displayName}
            </TruncatedText>
            {ao.email && ao.name && (
              <TruncatedText size="sm" c="dimmed">
                {ao.email}
              </TruncatedText>
            )}
          </Box>
        )
      })}
      {overflowCount > 0 && (
        <Popover width={280} position="bottom-start" shadow="md" withArrow>
          <Popover.Target>
            <Text size="xs" c="dimmed" mt={4} style={{ cursor: 'pointer' }} td="underline">
              +{overflowCount} more
            </Text>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap={8}>
              {value.additionalOwners.slice(MAX_VISIBLE_ADDITIONAL_OWNERS).map(ao => {
                const displayName = ao.name || ao.email || ao.userId
                return (
                  <Box key={ao.userId} style={{ lineHeight: 1.3 }}>
                    <Text size="sm" fw={600}>
                      {displayName}
                    </Text>
                    {ao.email && ao.name && (
                      <Text size="sm" c="dimmed">
                        {ao.email}
                      </Text>
                    )}
                  </Box>
                )
              })}
            </Stack>
          </Popover.Dropdown>
        </Popover>
      )}
    </Box>
  )
}

function NonAdminMenuItems({
  cm,
  userId,
  status,
  transcodeSuccess,
  isOwner,
  menuIconStyle,
  changeStatus,
  copyModule,
}: {
  cm: GetContentModuleResponse
  userId: string
  status: string
  transcodeSuccess: boolean | undefined
  isOwner: boolean
  menuIconStyle: React.CSSProperties
  changeStatus: (status: `${ContentModuleStatus}`) => () => void
  copyModule: () => void
}) {
  const hasDirectPublish = getUserOwnerEntry(cm, userId)?.directPublish ?? false

  return (
    <>
      <Menu.Item
        leftSection={<IconArchive style={menuIconStyle} />}
        disabled={status === 'draft' || status === 'pending_approval' || (status === 'published' && !hasDirectPublish)}
        onClick={changeStatus('draft')}>
        Back to Draft
      </Menu.Item>
      {hasDirectPublish && status === 'published' && (
        <Menu.Item leftSection={<IconClockShare style={menuIconStyle} />} onClick={changeStatus('pending_approval')}>
          Back to Pending
        </Menu.Item>
      )}
      <Menu.Item
        leftSection={<IconSettingsCog style={menuIconStyle} />}
        disabled={status === 'testing' || status === 'pending_approval' || status === 'published'}
        onClick={changeStatus('testing')}>
        Test in App
      </Menu.Item>
      <Menu.Item
        leftSection={<IconClockShare style={menuIconStyle} />}
        disabled={status === 'pending_approval' || status === 'published' || transcodeSuccess === false}
        onClick={changeStatus('pending_approval')}>
        Submit for Approval
      </Menu.Item>
      {hasDirectPublish && (
        <Menu.Item
          leftSection={<IconSend2 style={menuIconStyle} />}
          disabled={status === 'published' || status === 'removed' || transcodeSuccess === false}
          onClick={changeStatus('published')}>
          Publish
        </Menu.Item>
      )}
      <Menu.Item
        color="red.7"
        leftSection={<IconTrash style={menuIconStyle} />}
        disabled={status === 'removed' || status === 'pending_approval' || status === 'published'}
        onClick={changeStatus('removed')}>
        Delete
      </Menu.Item>
      <Menu.Item leftSection={<IconCopy style={menuIconStyle} />} disabled={!isOwner} onClick={copyModule}>
        Copy
      </Menu.Item>
    </>
  )
}

const columnHelper = createMRTColumnHelper<GetContentModulesResponse[0]>()

const cols: MRT_ColumnDef<GetContentModulesResponse[0]>[] = [
  columnHelper.accessor('title', {
    id: ColId.Title,
    header: 'Title',
    grow: true,
  }),
  columnHelper.accessor('status', {
    id: ColId.Status,
    header: 'Status',
    grow: false,
    size: 160,
    Cell: props => {
      const status = props.cell.getValue() ?? 'draft'
      const inProcess = props.row.original.inProcess
      const { changeModuleStatus, copyContentModule, deletingModules } = useTableContext()
      const { authenticationStore } = useGlobalState()
      const { user, isSponsorAdmin } = authenticationStore

      const cm = props.row.original
      const isOwner = isOwnerOrAdditionalOwner(cm, user?.userId ?? '')
      const enableActionsMenu = isSponsorAdmin || isOwner || cm.editable

      const isAllSponsorsModule = cm.allowedForAllSponsors ?? false

      const menuIconStyle = { width: theme.rem(18), height: theme.rem(18) }
      const changeStatus = (status: `${ContentModuleStatus}`) => () => changeModuleStatus(props.row.original, status)
      const taskId = props.row.original.taskId ?? ''
      const copyModule = () => copyContentModule(taskId)

      const hasError = props.row.original.error?.hasError
      const transcodeCompleted = props.row.original.transcodeCompleted
      const transcodeSuccess = props.row.original.transcodeSuccess
      const hasTranscodeError = transcodeCompleted === true && transcodeSuccess === false && status !== 'published'
      const isPublishedWithTranscodeFailure =
        status === 'published' && transcodeCompleted === true && transcodeSuccess === false

      useEffect(() => {
        if (isPublishedWithTranscodeFailure) {
          console.warn(
            `CM "${props.row.original.title}" (${props.row.original.taskId}) is published but has a transcode failure`,
          )
        }
      }, [isPublishedWithTranscodeFailure, props.row.original.title, props.row.original.taskId])

      const statusBadge = (
        <Button
          variant="filled"
          size="xs"
          color={
            {
              published: 'green',
              draft: 'orange',
              removed: 'gray',
              pending_approval: 'blue',
              testing: 'yellow',
              complete: 'blue', // TODO: remove once backend implements `pending_approval` status
            }[status]
          }
          rightSection={<IconChevronDown size={16} stroke={1.5} />}
          pr={12}
          radius="md">
          {
            // @ts-expect-error - `complete` status is deprecated
            (status === 'complete' || status === 'pending_approval' ? 'Pending' : status).toUpperCase()
          }
        </Button>
      )

      const errorTooltipMessage = hasTranscodeError
        ? "There was a problem processing one or more files in this content module. Please review the module's content."
        : 'There was an error publishing this content module. Please try again or contact support.'
      const errorIcon =
        hasError || hasTranscodeError ? (
          <Tooltip label={errorTooltipMessage} multiline w={220} withArrow>
            <Box>
              <IconAlertCircle size={20} color="red" style={{ display: 'block' }} />
            </Box>
          </Tooltip>
        ) : null

      const isDeleting = deletingModules.has(props.row.original.taskId ?? '')

      return (
        <Group gap="xs" wrap="nowrap">
          {isDeleting ? (
            <Badge variant="filled" color="red" rightSection={<Loader size="xs" color="white" type="dots" />}>
              DELETING
            </Badge>
          ) : inProcess ? (
            <Badge variant="filled" color="gray" rightSection={<Loader size="xs" color="white" type="dots" />}>
              PROCESSING
            </Badge>
          ) : transcodeCompleted === false ? (
            <Badge variant="filled" color="yellow" rightSection={<Loader size="xs" color="white" type="dots" />}>
              PROCESSING
            </Badge>
          ) : enableActionsMenu ? (
            <>
              <Menu withArrow arrowPosition="center">
                <Menu.Target>
                  <Box style={{ cursor: 'pointer' }}>{statusBadge}</Box>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Label>Change Status:</Menu.Label>
                  {isSponsorAdmin ? (
                    <>
                      <Menu.Item
                        leftSection={<IconArchive style={menuIconStyle} />}
                        disabled={
                          !canSponsorAdminMoveToDraft(status as ContentModuleStatus, isOwner, isAllSponsorsModule)
                        }
                        onClick={changeStatus('draft')}>
                        Back to Draft
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconClockShare style={menuIconStyle} />}
                        disabled={
                          !canSponsorAdminMoveToPendingApproval(
                            status as ContentModuleStatus,
                            isOwner,
                            isAllSponsorsModule,
                          )
                        }
                        onClick={changeStatus('pending_approval')}>
                        Back to Pending
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconSettingsCog style={menuIconStyle} />}
                        disabled={
                          !canSponsorAdminMoveToTesting(status as ContentModuleStatus, isOwner, isAllSponsorsModule)
                        }
                        onClick={changeStatus('testing')}>
                        Test in App
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconSend2 style={menuIconStyle} />}
                        disabled={
                          !canSponsorAdminPublish(status as ContentModuleStatus, isOwner, isAllSponsorsModule) ||
                          transcodeSuccess === false
                        }
                        onClick={changeStatus('published')}>
                        Publish
                      </Menu.Item>
                      <Menu.Item
                        color="red.7"
                        leftSection={<IconTrash style={menuIconStyle} />}
                        disabled={!canSponsorAdminDelete(status as ContentModuleStatus, isOwner, isAllSponsorsModule)}
                        onClick={changeStatus('removed')}>
                        Delete
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconCopy style={menuIconStyle} />}
                        disabled={!isOwner}
                        onClick={copyModule}>
                        Copy
                      </Menu.Item>
                    </>
                  ) : (
                    <NonAdminMenuItems
                      cm={cm}
                      userId={user?.userId ?? ''}
                      status={status}
                      transcodeSuccess={transcodeSuccess}
                      isOwner={isOwner}
                      menuIconStyle={menuIconStyle}
                      changeStatus={changeStatus}
                      copyModule={copyModule}
                    />
                  )}
                </Menu.Dropdown>
              </Menu>
              {errorIcon}
            </>
          ) : (
            <>
              {statusBadge}
              {errorIcon}
            </>
          )}
        </Group>
      )
    },
  }),
  columnHelper.accessor(
    row => {
      return row.categories?.[0] ?? ''
    },
    {
      id: ColId.Topic,
      header: 'Topic',
      grow: false,
      size: 180,
      Cell: ({ row, cell }) => {
        const { topicsData, updateTopic, updatingTopicIds } = useTableContext()
        const { authenticationStore } = useGlobalState()
        const { isSponsorAdmin } = authenticationStore
        const currentTopic = cell.getValue<string>()
        const taskId = row.original.taskId
        const isPending = taskId ? updatingTopicIds.has(taskId) : false

        // Sponsor admins get an inline topic selector
        if (isSponsorAdmin && taskId) {
          const options: TopicOption[] = [{ value: '', label: 'None' }, ...topicsData]

          // Box carries the wait cursor; Mantine's `disabled` sets
          // pointer-events: none on the input, which swallows cursor styles.
          return (
            <Box style={{ cursor: isPending ? 'wait' : undefined }}>
              <Select
                size="xs"
                data={options}
                value={currentTopic || ''}
                onChange={value => {
                  updateTopic(taskId, value || null)
                }}
                placeholder="Select topic"
                clearable={false}
                allowDeselect={false}
                disabled={isPending}
                rightSection={isPending ? <Loader size="xs" /> : undefined}
                rightSectionPointerEvents="none"
                styles={{
                  input: {
                    minHeight: '28px',
                    height: '28px',
                    opacity: isPending ? 0.55 : 1,
                    backgroundColor: isPending ? 'var(--mantine-color-gray-0)' : undefined,
                  },
                }}
                onClick={e => e.stopPropagation()}
              />
            </Box>
          )
        }

        // Non-admins see plain text
        return currentTopic ? (
          <Text size="sm">{currentTopic}</Text>
        ) : (
          <Text size="sm" c="dimmed">
            No topic
          </Text>
        )
      },
    },
  ),
  columnHelper.accessor(
    // Default to 'free' when null/undefined per spec.
    row => (row.subscriptionOnly === true ? 'subscriber' : 'free') satisfies MediaAccessValue,
    {
      id: ColId.MediaAccess,
      header: 'Media Access',
      grow: false,
      size: 200,
      Header: ({ column }) => (
        <HeaderCellWithTooltip
          column={column}
          header={<>Media Access</>}
          tooltip="Whether the primary file is available to guest users or is only available to Subscribers."
        />
      ),
      Cell: ({ row, cell }) => {
        const { updateMediaAccess, isUpdatingMediaAccess } = useTableContext()
        const { authenticationStore } = useGlobalState()
        const { isSponsorAdmin } = authenticationStore
        const value = cell.getValue<MediaAccessValue>()
        const taskId = row.original.taskId
        const isSubscriber = value === 'subscriber'
        const Icon = isSubscriber ? IconUsers : IconWorld
        const label = isSubscriber ? 'SUBSCRIBER' : 'FREE'

        // Column is only mounted when guestUserAccess && isSponsorAdmin (see
        // visibility), but guard `taskId` and the admin flag here too — a CM
        // row mid-creation can briefly lack a taskId.
        if (!isSponsorAdmin || !taskId) {
          return (
            <Group gap={6} wrap="nowrap" c="indigo.6">
              <Icon size={14} stroke={1.5} />
              <Text size="xs" fw={600}>
                {label}
              </Text>
            </Group>
          )
        }

        return (
          <Menu withArrow arrowPosition="center" position="bottom-start" disabled={isUpdatingMediaAccess}>
            <Menu.Target>
              <Button
                variant="default"
                size="xs"
                radius="md"
                c="indigo.6"
                leftSection={<Icon size={14} stroke={1.5} />}
                rightSection={<IconChevronDown size={14} stroke={1.5} />}
                onClick={e => e.stopPropagation()}
                disabled={isUpdatingMediaAccess}
                styles={{ root: { fontWeight: 600 }, label: { letterSpacing: '0.02em' } }}>
                {label}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconWorld size={14} stroke={1.5} />}
                disabled={value === 'free'}
                onClick={() => updateMediaAccess(taskId, 'free')}>
                Free
              </Menu.Item>
              <Menu.Item
                leftSection={<IconUsers size={14} stroke={1.5} />}
                disabled={value === 'subscriber'}
                onClick={() => updateMediaAccess(taskId, 'subscriber')}>
                Subscriber
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )
      },
    },
  ),
  columnHelper.accessor(
    row => {
      if (row.visibleToMsk) {
        return MSK_NAME
      }
      const { esks } = useTableContext()
      const selectEsk = row.selectEsk

      // Handle PSK case - show "Personal Sidekick" instead of "PSK"
      if (selectEsk === 'PSK') {
        return MSK_NAME
      }

      return esks.find(e => e.eskId === selectEsk)?.name ?? null
    },
    {
      id: ColId.EskName,
      header: '',
      grow: false,
      size: 138,
      Header: ({ column }) => (
        <HeaderCellWithTooltip column={column} header={<>Sidekick</>} tooltip="The Sidekick assigned to a module" />
      ),
      Cell: ({ cell }) => {
        const { isEskDataReady } = useTableContext()
        const value = cell.getValue<string | null>()

        // Show skeleton while ESK data is loading
        if (!isEskDataReady) {
          return <Skeleton height={16} width={80} radius="sm" />
        }

        return value ?? ''
      },
    },
  ),
  columnHelper.accessor(
    row => {
      const { users } = useTableContext()
      const createdBy = row.createdBy
      const extraOwners = (row.additionalOwners ?? []).filter(ao => ao.userId !== createdBy)
      if (!createdBy) return { display: '', name: '', email: '', additionalOwners: extraOwners }

      const user = users.find(u => u.userId === createdBy)
      if (!user) {
        const fallback = row.owner ?? 'Unknown'
        return { display: fallback, name: fallback, email: '', additionalOwners: extraOwners }
      }

      return {
        display: user.name || user.email,
        name: user.name || '',
        email: user.email || '',
        additionalOwners: extraOwners,
      }
    },
    {
      id: ColId.HumanExpert,
      header: 'Owner',
      grow: false,
      size: 260,
      Cell: ({ cell }) => {
        const { isSponsorAdmin } = useGlobalState().authenticationStore
        const value = cell.getValue<OwnerCellValue>()
        return <OwnerCell value={value} showDetailedView={isSponsorAdmin} />
      },
    },
  ),
]
